import { ArrowTopRightOnSquareIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { useCallback, useMemo, useState } from "react";

import { AssignmentEntityType, UserInfo } from "@/api/rest/models/account";
import { ChatPresetQuestionsConfiguration, TallyFormConfiguration } from "@/api/rest/models/configurations";
import {
  EntityConfiguration,
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useBuildingConfiguration,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import {
  AISettings,
  ChatPresetQuestionsSettings,
  EmailForwardingConfiguration,
  EmailForwardingSettings,
  BuildingHomeownersDashboard,
  HausingSettings,
  HouseholdSettings,
  StructuredWarrantySettings,
  TallyFormSettings,
  WarrantySettings,
} from "@/components/building";
import { StructuredWarrantyRules } from "@/api/rest/services/warrantyService";
import ConfigurationEditor from "@/components/building/ConfigurationEditor";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/common/Accordeon";
import Button from "@/components/common/Button";
import Checkbox from "@/components/common/Checkbox";
import Spinner from "@/components/common/Spinner";
import UserManagement from "@/components/common/UserManagement";
import { getConfigurationTypeLabel } from "@/utils/configurationTypeLabels";
import { isDobuEmployee } from "@/utils/userUtils";

// Base configuration types available to all users with their display order
const baseConfigurationTypes = new Map<EntityConfigurationType, number>([[EntityConfigurationType.WELCOME_EMAIL, 0]]);

// Additional configuration types for Dobu employees with their display order
const dobuEmployeeConfigTypes = new Map<EntityConfigurationType, number>([
  [EntityConfigurationType.WELCOME_EMAIL, 0],
  [EntityConfigurationType.FRONTEND_FEATURE_FLAGS, 1],
]);

export default function BuildingSettingsPage() {
  const { t } = useTranslation();
  const { buildingId } = useParams<{ buildingId: string }>();
  const navigate = useNavigate();

  // Get user info from context
  const { user } = useOutletContext<{ user: UserInfo | null }>();

  const {
    data: buildingConfiguration,
    isLoading,
    error,
    refetch: refreshBuildingConfiguration,
  } = useBuildingConfiguration(Number(buildingId));

  const currentBuilding = useMemo(
    () =>
      user?.accounts
        .flatMap((account) => account.managedBuildings)
        .find((building) => building.buildingId === Number(buildingId)),
    [user?.accounts, buildingId],
  );
  const isBuildingOwner = currentBuilding !== undefined;

  const forwardingConfiguration = useMemo(() => {
    const config = buildingConfiguration?.find(
      (conf) => conf.type === EntityConfigurationType.ESCALATION_QUESTION_EMAIL_FORWARDING_RULE,
    );
    if (config?.value) {
      try {
        return JSON.parse(config.value) as EmailForwardingConfiguration;
      } catch (e) {
        console.error("Failed to parse forwarding rules:", e);
      }
    }
    return { enabled: false, rules: [] };
  }, [buildingConfiguration]);

  const tallyFormConfiguration = useMemo(() => {
    const config = buildingConfiguration?.find((conf) => conf.type === EntityConfigurationType.TALLY_FORM);
    if (config?.value) {
      try {
        return JSON.parse(config.value) as TallyFormConfiguration;
      } catch (e) {
        console.error("Failed to parse TallyForm configuration:", e);
      }
    }
    return { enabled: false, forms: [] };
  }, [buildingConfiguration]);

  const chatPresetQuestionsConfiguration = useMemo(() => {
    const config = buildingConfiguration?.find((conf) => conf.type === EntityConfigurationType.CHAT_PRESET_QUESTIONS);
    if (config?.value) {
      try {
        return JSON.parse(config.value) as ChatPresetQuestionsConfiguration;
      } catch (e) {
        console.error("Failed to parse Chat Preset Questions configuration:", e);
      }
    }
    return { enabled: false, presets: [] };
  }, [buildingConfiguration]);

  const structuredWarrantyConfiguration = useMemo(() => {
    const config = buildingConfiguration?.find(
      (conf) => conf.type === EntityConfigurationType.STRUCTURED_WARRANTY_RULES,
    );
    if (config?.value) {
      try {
        return JSON.parse(config.value) as StructuredWarrantyRules;
      } catch (e) {
        console.error("Failed to parse Structured Warranty configuration:", e);
      }
    }
    return { enabled: false, subcontractors: [], defaultCategory: null };
  }, [buildingConfiguration]);

  const lookerReportLink = useMemo(() => {
    const config = buildingConfiguration?.find((conf) => conf.type === EntityConfigurationType.LOOKER_REPORT_LINK);
    if (!config?.value) {
      return null;
    }

    const trimmedValue = config.value.trim();

    try {
      const url = new URL(trimmedValue);
      return url.toString();
    } catch (error) {
      console.warn("Invalid Looker report link configuration", error);
      return null;
    }
  }, [buildingConfiguration]);

  const lookerReportConfiguration = useMemo(() => {
    const config = buildingConfiguration?.find((conf) => conf.type === EntityConfigurationType.LOOKER_REPORT_LINK);
    if (config) {
      return config;
    }
    return {
      id: -1,
      type: EntityConfigurationType.LOOKER_REPORT_LINK,
      entityType: EntityConfigurationEntityType.BUILDING,
      entityId: Number(buildingId),
      value: "",
    };
  }, [buildingConfiguration, buildingId]);

  const internalAnalyticsEnabled = useMemo(() => {
    const config = buildingConfiguration?.find(
      (conf) => conf.type === EntityConfigurationType.INTERNAL_ANALYTICS_ENABLED,
    );
    return config?.value === "true";
  }, [buildingConfiguration]);

  const { mutateAsync: updateConfiguration } = useUpdateConfiguration();
  const [isSavingAnalytics, setIsSavingAnalytics] = useState(false);

  const handleInternalAnalyticsChange = useCallback(
    async (enabled: boolean) => {
      setIsSavingAnalytics(true);
      try {
        await updateConfiguration({
          entityType: EntityConfigurationEntityType.BUILDING,
          buildingId: Number(buildingId),
          configurationType: EntityConfigurationType.INTERNAL_ANALYTICS_ENABLED,
          configurationValue: enabled ? "true" : "false",
        });
        refreshBuildingConfiguration();
      } catch (error) {
        console.error("Failed to update internal analytics setting:", error);
      } finally {
        setIsSavingAnalytics(false);
      }
    },
    [buildingId, updateConfiguration, refreshBuildingConfiguration],
  );

  // Determine which configuration types to show based on user's email
  const allowedConfigurationTypes = useMemo(() => {
    return isDobuEmployee(user) ? dobuEmployeeConfigTypes : baseConfigurationTypes;
  }, [user]);

  // Create a map of all allowed configuration types with their current values
  const configurationsToDisplay = useMemo(() => {
    const configMap = new Map<EntityConfigurationType, EntityConfiguration>();

    // First, add all existing configurations from the database
    buildingConfiguration?.forEach((conf) => {
      if (allowedConfigurationTypes.has(conf.type)) {
        configMap.set(conf.type, conf);
      }
    });

    // Build the display array sorted by position from allowedConfigurationTypes Map
    return Array.from(allowedConfigurationTypes.entries())
      .sort(([, posA], [, posB]) => posA - posB)
      .map(([type]) => {
        if (configMap.has(type)) {
          return configMap.get(type)!;
        }
        // Create a placeholder configuration for types that don't exist yet
        return {
          id: -1, // Temporary ID for new configurations
          type: type,
          entityType: buildingConfiguration?.[0]?.entityType || EntityConfigurationEntityType.BUILDING,
          entityId: Number(buildingId),
          value: "",
        };
      });
  }, [buildingConfiguration, allowedConfigurationTypes, buildingId]);

  if (isLoading) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return <div className="flex flex-grow items-center justify-center text-red-600">{t("common.error.loading")}</div>;
  }

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row">
          <ChevronLeftIcon
            className="h-9 w-9 mr-2 hover:bg-slate-300 focus-visible:bg-dobu-orange cursor-pointer rounded"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-3xl font-bold text-center mr-4">{t("buildingSettings.title")}</h2>
        </div>

        {Number(buildingId) && (
          <div className="mt-8 rounded-lg border border-gray-200 bg-white p-4">
            <UserManagement entityId={Number(buildingId)} entityType={AssignmentEntityType.BUILDING} />
          </div>
        )}

        {Number(buildingId) && <BuildingHomeownersDashboard buildingId={Number(buildingId)} />}

        {/* Add section title for Dobu employees */}
        {isDobuEmployee(user) && (
          <div className="mt-8 text-xl font-semibold">{t("buildingSettings.employeeSettings")}</div>
        )}

        <Accordion type="multiple" className="w-full mt-4 border border-gray-200 border-lg rounded-lg overflow-hidden">
          <AccordionItem value="email-forwarding">
            <AccordionTrigger className="font-semibold">{t("buildingSettings.emailForwarding")}</AccordionTrigger>
            <AccordionContent>
              <EmailForwardingSettings initialConfiguration={forwardingConfiguration} buildingId={Number(buildingId)} />
            </AccordionContent>
          </AccordionItem>

          {isDobuEmployee(user) && (
            <AccordionItem value="tally-form">
              <AccordionTrigger className="font-semibold">{t("buildingSettings.tallyForms")}</AccordionTrigger>
              <AccordionContent>
                <TallyFormSettings initialConfiguration={tallyFormConfiguration} buildingId={Number(buildingId)} />
              </AccordionContent>
            </AccordionItem>
          )}

          {isDobuEmployee(user) && (
            <AccordionItem value="chat-preset-questions">
              <AccordionTrigger className="font-semibold">{t("buildingSettings.chatPresetQuestions")}</AccordionTrigger>
              <AccordionContent>
                <ChatPresetQuestionsSettings
                  initialConfiguration={chatPresetQuestionsConfiguration}
                  buildingId={Number(buildingId)}
                />
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="ai-settings">
            <AccordionTrigger className="justify-between font-semibold">
              {t("buildingSettings.aiSettings")}
            </AccordionTrigger>
            <AccordionContent>
              <AISettings
                buildingConfiguration={buildingConfiguration || []}
                buildingId={Number(buildingId)}
                refreshBuildingConfiguration={refreshBuildingConfiguration}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="warranty-settings">
            <AccordionTrigger className="justify-between font-semibold">
              {t("buildingSettings.warrantySettings")}
            </AccordionTrigger>
            <AccordionContent>
              <WarrantySettings
                buildingConfiguration={buildingConfiguration || []}
                buildingId={Number(buildingId)}
                primaryLanguage={currentBuilding?.primaryLanguage ?? "en"}
                supportedLanguages={currentBuilding?.supportedLanguages ?? ["en"]}
                refreshBuildingConfiguration={refreshBuildingConfiguration}
              />
            </AccordionContent>
          </AccordionItem>

          {isDobuEmployee(user) && (
            <AccordionItem value="structured-warranty-settings">
              <AccordionTrigger className="justify-between font-semibold">
                {t("buildingSettings.structuredWarrantySettings")}
              </AccordionTrigger>
              <AccordionContent>
                <StructuredWarrantySettings
                  initialConfiguration={structuredWarrantyConfiguration}
                  buildingId={Number(buildingId)}
                />
              </AccordionContent>
            </AccordionItem>
          )}

          {isBuildingOwner && (
            <AccordionItem value="hausing-settings">
              <AccordionTrigger className="justify-between font-semibold">
                {t("buildingSettings.hausingSettings")}
              </AccordionTrigger>
              <AccordionContent>
                <HausingSettings buildingId={Number(buildingId)} />
              </AccordionContent>
            </AccordionItem>
          )}

          {isDobuEmployee(user) && (
            <AccordionItem value="analytics-settings">
              <AccordionTrigger className="justify-between font-semibold">
                {t("buildingSettings.analyticsSettings")}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Checkbox
                      label={t("buildingSettings.enableInternalAnalytics")}
                      checked={internalAnalyticsEnabled}
                      onChange={(e) => handleInternalAnalyticsChange(e.target.checked)}
                      disabled={isSavingAnalytics}
                    />
                    {internalAnalyticsEnabled && (
                      <Button className="gap-2 rounded-lg" onClick={() => navigate("/analytics/")}>
                        {t("buildingSettings.viewAnalytics")}
                      </Button>
                    )}
                    {lookerReportLink && (
                      <a
                        href={lookerReportLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-custom inline-flex items-center gap-2 rounded-lg bg-dobu-orange px-4 py-2 font-medium text-white shadow transition hover:bg-orange-500"
                      >
                        {t("buildingSettings.viewLookerReport")}
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("buildingSettings.lookerReportLink")}
                    </label>
                    <ConfigurationEditor conf={lookerReportConfiguration} refreshConf={refreshBuildingConfiguration} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {isDobuEmployee(user) && (
            <AccordionItem value="household-settings">
              <AccordionTrigger className="font-semibold">{t("buildingSettings.householdSettings")}</AccordionTrigger>
              <AccordionContent>
                <HouseholdSettings buildingId={Number(buildingId)} isDobuEmployee={isDobuEmployee(user)} />
              </AccordionContent>
            </AccordionItem>
          )}

          {configurationsToDisplay.map((conf) => (
            <AccordionItem value={conf.type} key={conf.type}>
              <AccordionTrigger className="font-semibold">{getConfigurationTypeLabel(t, conf.type)}</AccordionTrigger>
              <AccordionContent>
                <ConfigurationEditor conf={conf} refreshConf={refreshBuildingConfiguration} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </main>
  );
}
